/**
 * The voice index (R22) — finding what she said.
 *
 * @remarks
 * Calder's Phase 5 argument, made mechanism: a representative exported
 * session is 141 lines and her twelve are the reason the file is worth
 * sending — yet the game shelves documents and refuses to index her.
 * "Finding," not "logging": the transcript already logs everything; what was
 * missing is going back to ONE thing she said. The key is **what you were
 * doing when she said it**, and every entry already carries that as
 * `triggerContext` — she comments on the ACT, so the act is how a player
 * looks ("the thing she said when I opened the locker").
 *
 * The `deriveJournal` precedent, narrator-side: a pure derived read over the
 * committed transcript — no new state, no migration, replay-safe by
 * construction. The channel is a PARAMETER (his §7.4: a narrator is a channel
 * with an index and a quotation law, not a character object) — and since
 * D192 the pack declares its speakers, so the host asks once per speaker.
 *
 * Text is FROZEN, deliberately opposite the journal's live readings: the
 * journal is the player re-reading a document now, so its conditional lines
 * re-resolve; the index recalls what she SAID, and she said what she said.
 * The transcript is the record (D86), and this module only reads it back.
 *
 * Labels are engine-neutral (the D24 posture: the mechanics live here and are
 * test-pinned; the wording claims no character). Known limit, recorded in the
 * D92 decision rather than solved here: a voice line spoken by an occurrence
 * listener is stamped `voice:<id>`, so the act that provoked it is not on the
 * entry — enriching that stamp would change committed state, which is a
 * design question, not a rider.
 *
 * @packageDocumentation
 */

import type { ItemId, RoomId, RuntimeState, RuntimeTranscriptEntry } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';

import { groupTranscriptEntries } from '../transcript/group-transcript-entries.js';

/** The structured reading of what the player was doing when the line spoke. */
export type VoiceIndexAct =
	| { readonly kind: 'arrival'; readonly roomName: string }
	| { readonly kind: 'authored_beat' }
	| { readonly kind: 'ending' }
	| { readonly kind: 'engine_response'; readonly response: 'empty_directive' | 'refusal' | 'teach' }
	| { readonly kind: 'unattributed' }
	| {
		readonly kind: 'verb';
		readonly objectName?: string;
		readonly subjectName?: string;
		readonly verb: string;
	};

/** One indexed utterance, in transcript order. */
export interface VoiceIndexEntry {
	/** The structured act — what the player was doing (see {@link VoiceIndexAct}). */
	readonly act: VoiceIndexAct;
	/** The act rendered as one engine-neutral line. */
	readonly actLabel: string;
	/**
	 * The player's own echoed words for the input the line landed in, when its
	 * turn group has one — the human key beside the act's robust one.
	 */
	readonly duringInput?: string;
	/** The line VERBATIM from the transcript — frozen, never re-resolved. */
	readonly text: string;
	/** The turn the line landed on (orientation against the export). */
	readonly turnCount: number;
}

/**
 * Derive the voice index: every line the channel spoke, in order, each keyed
 * by the act it commented on.
 *
 * @param bundle - The loaded bundle (names for the act labels)
 * @param state - The live runtime state (the transcript is read, never written)
 * @param source - The speaker to index (D192: one call per declared speaker; the host makes them)
 * @returns The channel's utterances, oldest first (empty until it speaks)
 */
export const deriveVoiceIndex = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	source: RuntimeTranscriptEntry['source'],
): readonly VoiceIndexEntry[] => {
	// One fold, reused: the entry's turn group carries the player's echo as
	// its heading (R18's seam), which is `duringInput` here.
	const echoByEntryId = new Map<string, string>();

	for (const group of groupTranscriptEntries(state.session.transcript)) {
		if (group.headingEntry !== undefined) {
			for (const entry of group.entries) {
				echoByEntryId.set(entry.id, group.headingEntry.text);
			}
		}
	}

	return state.session.transcript
		.filter((entry) => entry.source === source)
		.map((entry) => {
			const act = readAct(bundle, entry.triggerContext, wasEmptyDirective(entry.id, state));
			const duringInput = echoByEntryId.get(entry.id);

			return {
				act,
				actLabel: labelOf(act),
				...(duringInput === undefined ? {} : { duringInput }),
				text: entry.text,
				turnCount: entry.turnCount,
			};
		});
};

/** The exact contexts that are the engine answering, not the player acting. */
const ENGINE_RESPONSE_CONTEXTS: Readonly<Record<string, 'refusal' | 'teach'>> = {
	action_rejected: 'refusal',
	parse_teach: 'teach',
};

/** Context prefixes that mean an authored beat spoke — the pack's own voice, not a player act. */
const AUTHORED_BEAT_PREFIXES = ['event:', 'trigger:', 'voice:'] as const;

/**
 * Contexts that attribute a line to the CONVERSATION's machinery rather than
 * to anything the player did to the world — the index says no more than the
 * record does.
 */
const UNATTRIBUTED_CONTEXTS = new Set(['chain_abandoned', 'map', 'player_input', 'reaction']);

/**
 * Was this entry's input a blank line? (Calder's R22 register note: a blank
 * input's teach must not index as "an input that did not parse" — nothing was
 * input, and `an empty directive` is truer and in the brand's own words.
 * Derived, not stamped: the entry's `input-<n>-*` namespace names its input,
 * and the command history holds what was typed — nothing, in this case. A
 * blank input also gets no player echo, which is exactly why this one label
 * cannot lean on `duringInput` to rescue it.)
 *
 * @param entryId - The entry's transcript id
 * @param state - The live runtime state (the command history is the record of what was typed)
 * @returns Whether the entry's input was blank
 */
const wasEmptyDirective = (entryId: string, state: RuntimeState): boolean => {
	const inputOrdinalMatch = /^input-(?<ordinal>\d+)-/u.exec(entryId);
	const ordinalText = inputOrdinalMatch?.groups?.['ordinal'];

	return ordinalText !== undefined && state.session.commandHistory[Number(ordinalText) - 1]?.trim() === '';
};

/**
 * Read one `triggerContext` into a structured act.
 *
 * @param bundle - The loaded bundle (id → name lookups; misses fall back to the raw token — the index is a reader, never a gate)
 * @param triggerContext - The entry's provenance stamp, when it has one
 * @param isEmptyDirective - Whether the entry's input was blank (see {@link wasEmptyDirective})
 * @returns The structured act
 */
const readAct = (bundle: EngineGameBundle, triggerContext: string | undefined, isEmptyDirective: boolean): VoiceIndexAct => {
	if (triggerContext === undefined || UNATTRIBUTED_CONTEXTS.has(triggerContext) || triggerContext.includes('question_')) {
		return { kind: 'unattributed' };
	}

	const engineResponse = ENGINE_RESPONSE_CONTEXTS[triggerContext];

	if (engineResponse !== undefined) {
		return {
			kind: 'engine_response',
			response: engineResponse === 'teach' && isEmptyDirective ? 'empty_directive' : engineResponse,
		};
	}

	if (AUTHORED_BEAT_PREFIXES.some((prefix) => triggerContext.startsWith(prefix))) {
		return { kind: 'authored_beat' };
	}

	if (triggerContext.startsWith('ending:')) {
		return { kind: 'ending' };
	}

	const separatorIndex = triggerContext.indexOf(':');

	if (separatorIndex === -1) {
		// A bare verb context — `wait` is the shipped example: an act with no
		// object, exactly as the player performed it.
		return { kind: 'verb', verb: triggerContext };
	}

	const verb = triggerContext.slice(0, separatorIndex);
	const payload = triggerContext.slice(separatorIndex + 1);

	if (verb === 'first_visit') {
		return { kind: 'arrival', roomName: roomNameOf(bundle, payload) };
	}

	const [subjectToken, objectToken] = payload.split('>');
	const subjectName = subjectToken === undefined || subjectToken === '' ? undefined : itemNameOf(bundle, subjectToken);

	return {
		kind: 'verb',
		...(objectToken === undefined ? {} : { objectName: itemNameOf(bundle, objectToken) }),
		...(subjectName === undefined ? {} : { subjectName }),
		verb,
	};
};

/**
 * The label for each engine-response act — a record rather than a nested
 * switch, so the outer switch's cases all return directly (and a new response
 * kind is a compile error here, not a silent fallthrough).
 */
const ENGINE_RESPONSE_LABELS: Readonly<Record<Extract<VoiceIndexAct, { kind: 'engine_response' }>['response'], string>> = {
	empty_directive: 'an empty directive',
	refusal: 'a refused action',
	teach: 'an input that did not parse',
};

/**
 * Render one act as an engine-neutral label.
 *
 * @param act - The structured act
 * @returns One line, no character claimed
 */
const labelOf = (act: VoiceIndexAct): string => {
	switch (act.kind) {
		case 'arrival': {
			return `first entry — ${act.roomName}`;
		}
		case 'authored_beat': {
			return 'a story beat';
		}
		case 'ending': {
			return 'the ending';
		}
		case 'engine_response': {
			return ENGINE_RESPONSE_LABELS[act.response];
		}
		case 'unattributed': {
			return '—';
		}
		case 'verb': {
			const subjectPart = act.subjectName === undefined ? '' : ` — ${act.subjectName}`;
			const objectPart = act.objectName === undefined ? '' : ` on ${act.objectName}`;

			return `${act.verb}${subjectPart}${objectPart}`;
		}
	}
};

/**
 * An item id's display name, or the raw token when the bundle cannot name it.
 *
 * @param bundle - The loaded bundle
 * @param itemToken - The context's item token
 * @returns The name the label shows
 */
const itemNameOf = (bundle: EngineGameBundle, itemToken: string): string =>
	bundle.indexes.itemsById.get(itemToken as ItemId)?.name ?? itemToken;

/**
 * A room id's display name, or the raw token when the bundle cannot name it.
 *
 * @param bundle - The loaded bundle
 * @param roomToken - The context's room token
 * @returns The name the label shows
 */
const roomNameOf = (bundle: EngineGameBundle, roomToken: string): string =>
	bundle.indexes.roomsById.get(roomToken as RoomId)?.name ?? roomToken;
